import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import { Formik } from 'formik';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import LazyLoad from 'react-lazyload';
import {
  AppDatePicker,
  AppCard,
  AppRemixIcon,
  AppMultiSelect,
  AppNoInfo,
} from '@/layout/AppComponents';
import { createPortal } from 'react-dom';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import Translator from '../../../layout/components/helpers/Translator';
import AppInput from '../../../layout/components/form/AppInput';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import SingleLaw from '../../../layout/screenSpecifics/Modules/LawFinder/SingleLaw';
import RemixIcon from '../../../layout/components/helpers/RemixIcon';
import AppSpinner from '../../../layout/components/AppSpinner';
import AppLink from '../../../routing/helpers/AppLink';
import colors from '../../../constants/colors';
import { axiosPath, path } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import errorHandler from '@/layout/components/error/ErrorHandler';
import ClassifiedPlantSelect from '../../../layout/components/form/Selects/ClassifiedPlantSelect';

const ModuleLawFinderHomeScreen = () => {
  const { moduleBridgeHex, page, filters } = useParams();
  const [moduleData, setModuleData] = useState();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const userModulePermissionsLevel = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state?.auth?.permissions?.client);
  const [laws, setLaws] = useState(null);
  const [filtersInitialValues, setFiltersInitialValues] = useState();
  const [lawsDetails, setLawsDetails] = useState();
  const [exportReportIsLoading, setExportReportIsLoading] = useState(false);

  // > LOAD LAWS //
  const loadLaws = useCallback(async () => {
    setLaws(null);
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_getLaws'),
        { moduleBridgeHex, page, filters }
      );
      return response.data;
    } catch (err) {}
  }, [page, filters]);

  useEffect(() => {
    loadLaws().then((r) => {
      if (isMounted.current) {
        setLaws(r);
        setFiltersInitialValues(r.filters);
      }
    });
  }, [page, filters]);
  // > LOAD LAWS //

  const handleLawFilters = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_getLawEncryptedFilters'),
        values
      );
      navigate(
        path({
          name: 'user_module_lawFinder_home',
          params: { moduleBridgeHex, page: 1, filters: response.data },
        })
      );
    } catch (err) {}
  };

  // > LOAD LAWS DETAILS //
  const loadLawsDetails = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_getLawsDetails'),
        { moduleBridgeHex, page, filters }
      );
      return response.data;
    } catch (err) {}
  }, [page, filters]);

  useEffect(() => {
    if (laws) {
      loadLawsDetails().then((r) => {
        if (isMounted.current) {
          setLawsDetails(r);
        }
      });
    }
  }, [laws]);
  // > LOAD LAWS //

  const handleExportReport = async () => {
    try {
      setExportReportIsLoading(true);
      const response = await axios.post(
        axiosPath('api_r_user_module_lawFinder_exportReport'),
        { moduleBridgeHex, filters }
      );
      window.location = response.data;
      setExportReportIsLoading(false);
    } catch (err) {
      dispatch(errorHandler(err));
    }
  };

  const handleResetForm = (values, resetForm) => {
    resetForm();
    navigate(
      path({
        name: 'user_module_lawFinder_home',
        params: { moduleBridgeHex, page: 1, filters: 0 },
      })
    );
  };

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_lawFinder_getModuleData'), {
        moduleBridgeHex,
      })
      .then(({ data }) => setModuleData(data));
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="content-wrapper relative m_sr">
      <BreadcrumbHandler paths={[{ name: moduleData.moduleName }]} />
      {filtersInitialValues !== undefined && (
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          initialValues={{
            filterFreeText: laws?.filters?.filterFreeText || '',
            opensearch: laws?.filters?.opensearch || '',
            categories: laws?.filters?.categories || [],
            locations: laws?.filters?.locations || [],
            dateFrom: laws?.filters?.dateFrom || null,
            dateTo: laws?.filters?.dateTo || null,
            modificationDateFrom: laws?.filters?.modificationDateFrom || null,
            modificationDateTo: laws?.filters?.modificationDateTo || null,
            lawNumber: laws?.filters?.lawNumber || '',
            lawYear: laws?.filters?.lawYear || '',
            classifiedPlants: laws?.filters?.classifiedPlants || [],
          }}
          onSubmit={(v) => handleLawFilters(v)}
        >
          {({ setFieldValue, resetForm, handleSubmit, values, errors }) => (
            <>
              <section className="content mb-3">
                <div className="row">
                  <div className="col-12">
                    <AppCard
                      header={
                        <>
                          <span>
                            <Translator file="general" name="general.filters" />
                          </span>
                          <div className="float-right">
                            {filters != 0 && (
                              <span
                                onClick={() => handleResetForm(values, resetForm)}
                                className="btn btn-danger btn-xs mr-2"
                              >
                                <Translator file="filter" name="filter.delete" />
                              </span>
                            )}
                            <span
                              className="btn btn-info btn-xs"
                              onClick={handleSubmit}
                            >
                              <Translator file="filter" name="filter.apply" />
                            </span>
                          </div>
                        </>
                      }
                      footer={
                        <>
                          <small>
                            <Translator
                              file="general"
                              name="paginator.results-info"
                              variables={{
                                firstItemNumber: laws?.pagination.firstItemNumber,
                                lastItemNumber: laws?.pagination.lastItemNumber,
                                totalCount: laws?.pagination.totalCount,
                              }}
                              isHtml
                            />
                          </small>
                          {!auth?.user?.client?.demo &&
                            userModulePermissionsLevel !== 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-light float-right"
                                onClick={handleExportReport}
                                disabled={exportReportIsLoading}
                              >
                                <span className="d-inline-flex">
                                  {exportReportIsLoading ? (
                                    <SpinnerLoader size={12} />
                                  ) : (
                                    <AppRemixIcon icon="save-3" />
                                  )}
                                  <span className="ml-1">
                                    <Translator
                                      file="module-lawFinder"
                                      name="module.lf.export-lf"
                                    />
                                  </span>
                                </span>
                              </button>
                            )}
                        </>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <AppInput
                            title={<Translator file="filter" name="filter.by.law.exact" />}
                            placeholder={
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.law-text"
                              />
                            }
                            icon={<RemixIcon icon="auction" />}
                            onChange={(v) => setFieldValue('filterFreeText', v)}
                            value={values.filterFreeText}
                            isClearable
                          />
                          {permissions?.lawFinder?.opensearchFilter && (
                            <AppInput
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-lawFinder"
                                  name="module.lf.flexible.search"
                                />,
                              )}
                              placeholder={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-lawFinder"
                                  name="module.lf.works-even-words-missing-variations"
                                />,
                              )}
                              icon={<RemixIcon icon="crosshair" />}
                              onChange={(v) => setFieldValue('opensearch', v)}
                              value={values.opensearch}
                              isClearable
                            />
                          )}
                          <AppMultiSelect
                            options={moduleData.moduleCategories}
                            title={
                              <Translator file="evaluation" name="category.title" />
                            }
                            placeholder={ReactDOMServer.renderToString(
                              <Translator
                                file="user"
                                name="select.one.or.multiple.categories"
                              />,
                            )}
                            icon={<RemixIcon icon="font-color" noVariants />}
                            onChange={(v) => setFieldValue('categories', v)}
                            value={values.categories}
                            hasSearchBox
                            hasSelectOptions
                          />
                        </div>
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-lg-12">
                              <AppMultiSelect
                                options={moduleData.moduleLocations}
                                title={
                                  <Translator file="location" name="location.title" />
                                }
                                placeholder={ReactDOMServer.renderToString(
                                  <Translator
                                    file="location"
                                    name="location.select.one"
                                  />,
                                )}
                                icon={<RemixIcon icon="global" />}
                                onChange={(v) => setFieldValue('locations', v)}
                                value={values.locations}
                                hasSelectOptions
                                hasSearchBox
                                containerClassName="mb-0"
                              />
                            </div>
                          </div>
                          <div className="row">
                            {permissions?.lawFinder?.filterLawNumber && (
                              <div className="col-lg-6">
                                <AppInput
                                  title={
                                    <Translator file="filter" name="law.number" />
                                  }
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator file="filter" name="law.number" />,
                                  )}
                                  icon={<RemixIcon icon="auction" />}
                                  onChange={(v) => setFieldValue('lawNumber', v)}
                                  value={values.lawNumber}
                                  isClearable
                                />
                              </div>
                            )}
                            {permissions?.lawFinder?.filterLawYear && (
                              <div className="col-lg-6">
                                <AppInput
                                  title={<Translator file="filter" name="law.year" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator file="filter" name="law.year" />,
                                  )}
                                  icon={<RemixIcon icon="auction" />}
                                  onChange={(v) => setFieldValue('lawYear', v)}
                                  value={values.lawYear}
                                  isClearable
                                />
                              </div>
                            )}
                            {permissions?.lawFinder?.classifiedPlants && (
                              <div className="col-lg-12">
                                <ClassifiedPlantSelect
                                  value={values.classifiedPlants || []}
                                  onChange={(opt) =>
                                    setFieldValue('classifiedPlants', opt)
                                  }
                                  isMulti
                                  className="mt-0"
                                  isClearable
                                  onlyIcpe
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-3">
                          <AppDatePicker
                            title={`${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.creationStamp"
                              />,
                            )} (${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.from"
                              />,
                            )})`}
                            value={values.dateFrom}
                            onChange={(date) => setFieldValue('dateFrom', date)}
                            showMonthDropdown
                            showYearDropdown
                            isClearable
                            dropdownMode="select"
                            popperClassName="react-datepicker-popper-100"
                            popperContainer={({ children }) =>
                              createPortal(children, document.body)
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <AppDatePicker
                            title={`${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.creationStamp"
                              />,
                            )} (${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.to"
                              />,
                            )})`}
                            value={values.dateTo}
                            onChange={(date) => setFieldValue('dateTo', date)}
                            showMonthDropdown
                            showYearDropdown
                            isClearable
                            dropdownMode="select"
                            popperClassName="react-datepicker-popper-100"
                            popperContainer={({ children }) =>
                              createPortal(children, document.body)
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <AppDatePicker
                            title={`${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.modificationStamp"
                              />,
                            )} (${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.from"
                              />,
                            )})`}
                            value={values.modificationDateFrom}
                            onChange={(date) =>
                              setFieldValue('modificationDateFrom', date)
                            }
                            showMonthDropdown
                            showYearDropdown
                            isClearable
                            dropdownMode="select"
                            popperClassName="react-datepicker-popper-100"
                            popperContainer={({ children }) =>
                              createPortal(children, document.body)
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <AppDatePicker
                            title={`${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.modificationStamp"
                              />,
                            )} (${ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.to"
                              />,
                            )})`}
                            value={values.modificationDateTo}
                            onChange={(date) =>
                              setFieldValue('modificationDateTo', date)
                            }
                            showMonthDropdown
                            showYearDropdown
                            isClearable
                            dropdownMode="select"
                            popperClassName="react-datepicker-popper-100"
                            popperContainer={({ children }) =>
                              createPortal(children, document.body)
                            }
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12" />
                      </div>
                    </AppCard>
                  </div>
                </div>
              </section>
            </>
          )}
        </Formik>
      )}

      <div className="pb-4">
        {laws && laws.laws ? (
          laws.laws.length > 0 ? (
            laws.laws.map((law) => (
              <LazyLoad key={law.id} height={100}>
                <SingleLaw
                  law={law}
                  lawsDetails={lawsDetails}
                  evaluation={moduleData.evaluation}
                  evidences={moduleData.evidences}
                  articleObligations={moduleData.obligations}
                  moduleBridgeHex={moduleBridgeHex}
                />
              </LazyLoad>
            ))
          ) : (
            <div className="alert alert-info">
              <Translator
                file="module-legalCompliance"
                name="module.lc.export.law.no-results"
              />
            </div>
          )
        ) : (
          <NoInfo
            text=""
            icon={
              <AppSpinner>
                <RemixIcon icon="loader-4" size={24} color="#ccc" />
              </AppSpinner>
            }
          />
        )}
      </div>
      {laws && laws.laws.length > 0 && (
        <nav>
          <ul
            className="pagination"
            style={{ justifyContent: 'center', marginBottom: '4px' }}
          >
            <li className="page-item">
              <AppLink
                path="user_module_lawFinder_home"
                params={{
                  moduleBridgeHex,
                  page: laws?.pagination.first,
                  filters,
                }}
                className="page-link"
              >
                <span>&laquo;</span>
              </AppLink>
            </li>
            {laws.pagination.pagesInRange.map((p) => (
              <li
                key={p}
                className={`page-item ${laws.pagination.current === p && 'active'}`}
              >
                <AppLink
                  path="user_module_lawFinder_home"
                  params={{ moduleBridgeHex, page: p, filters }}
                  className="page-link"
                >
                  {p}
                </AppLink>
              </li>
            ))}
            <li className="page-item">
              <AppLink
                path="user_module_lawFinder_home"
                params={{
                  moduleBridgeHex,
                  page: laws?.pagination.last,
                  filters,
                }}
                className="page-link"
              >
                <span>&raquo;</span>
              </AppLink>
            </li>
          </ul>
          <p
            style={{
              fontSize: '10px',
              textAlign: 'center',
              marginBottom: '20px',
              color: colors.grey500,
            }}
          >
            <Translator
              file="general"
              name="paginator.results-info"
              variables={{
                firstItemNumber: laws?.pagination.firstItemNumber,
                lastItemNumber: laws?.pagination.lastItemNumber,
                totalCount: laws?.pagination.totalCount,
              }}
              isHtml
            />
          </p>
        </nav>
      )}
    </div>
  );
};

export default ModuleLawFinderHomeScreen;
